import { Card, Popconfirm } from 'antd';
import { Button } from '@bancobarigui/barihaus';
import { ToolOutlined, RollbackOutlined } from '@ant-design/icons';
import { InspectionReportReview } from '@/shared/types/inspectionReportReview.types';

interface ProviderCorrectionCardProps {
  pendingReview: InspectionReportReview | null;
  externalReturnData?: {
    reason: string;
    description: string | null;
  } | null;
  isDevolvidoAposAceite?: boolean;
  onCorrectClick: () => void;
  onRedirectToInspector?: () => void;
}
export function ProviderCorrectionCard({
  pendingReview,
  externalReturnData,
  isDevolvidoAposAceite,
  onCorrectClick,
  onRedirectToInspector,
}: ProviderCorrectionCardProps) {
  if (isDevolvidoAposAceite) {
    return (
      <Card style={{ marginBottom: 16, borderColor: '#fa541c' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 8, color: '#d4380d' }}>
              Laudo Devolvido pelo Banco (Pós-Aceite)
            </h4>
            {externalReturnData ? (
              <>
                <div style={{ marginBottom: 4 }}>
                  <strong style={{ color: '#666', fontSize: 13 }}>Motivo:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13 }}>
                    {externalReturnData.reason}
                  </p>
                </div>
                {externalReturnData.description && (
                  <div style={{ marginTop: 8 }}>
                    <strong style={{ color: '#666', fontSize: 13 }}>Descrição:</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                      {externalReturnData.description}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
                O banco devolveu o laudo após o aceite para correções. Corrija e reenvie o laudo.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            <Button
              buttonColor="primary"
              icon={<ToolOutlined />}
              onClick={onCorrectClick}
            >
              Corrigir Laudo
            </Button>
            {onRedirectToInspector && (
              <Popconfirm
                title="Devolver ao Inspetor"
                description="A inspeção será devolvida ao inspetor original para correção do checklist. Deseja continuar?"
                onConfirm={onRedirectToInspector}
                okText="Sim, devolver"
                cancelText="Cancelar"
              >
                <Button icon={<RollbackOutlined />}>
                  Devolver ao Inspetor
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>
      </Card>
    );
  }

  if (externalReturnData) {
    return (
      <Card style={{ marginBottom: 16, borderColor: '#faad14' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 8, color: '#d48806' }}>
              Laudo Devolvido pelo Sistema Externo
            </h4>
            <div style={{ marginBottom: 4 }}>
              <strong style={{ color: '#666', fontSize: 13 }}>Motivo:</strong>
              <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13 }}>
                {externalReturnData.reason}
              </p>
            </div>
            {externalReturnData.description && (
              <div style={{ marginTop: 8 }}>
                <strong style={{ color: '#666', fontSize: 13 }}>Descrição:</strong>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {externalReturnData.description}
                </p>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            <Button
              buttonColor="primary"
              icon={<ToolOutlined />}
              onClick={onCorrectClick}
            >
              Corrigir Laudo
            </Button>
            {onRedirectToInspector && (
              <Popconfirm
                title="Devolver ao Inspetor"
                description="A inspeção será devolvida ao inspetor original para correção do checklist. Deseja continuar?"
                onConfirm={onRedirectToInspector}
                okText="Sim, devolver"
                cancelText="Cancelar"
              >
                <Button icon={<RollbackOutlined />}>
                  Devolver ao Inspetor
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Se há pendingReview normal, mostra informações dele
  if (pendingReview) {
    return (
      <Card style={{ marginBottom: 16, borderColor: '#faad14' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: 0, marginBottom: 8, color: '#d48806' }}>
              O Banco apontou {pendingReview.fieldCorrections?.length || 0} correção(ões) no laudo
            </h4>
            {pendingReview.generalComments && (
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: '#666', fontSize: 13 }}>Comentários gerais:</strong>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {pendingReview.generalComments}
                </p>
              </div>
            )}
            {pendingReview.fieldCorrections && pendingReview.fieldCorrections.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong style={{ color: '#666', fontSize: 13 }}>Itens a corrigir:</strong>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 20, color: '#666', fontSize: 13 }}>
                  {pendingReview.fieldCorrections.map((correction, index) => (
                    <li key={index} style={{ marginBottom: 4 }}>
                      <strong>{correction.fieldLabel}</strong>
                      {correction.stepName && <span style={{ color: '#999' }}> ({correction.stepName})</span>}
                      : {correction.errorMessage}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
            <Button
              buttonColor="primary"
              icon={<ToolOutlined />}
              onClick={onCorrectClick}
            >
              Corrigir Laudo
            </Button>
            {onRedirectToInspector && (
              <Popconfirm
                title="Devolver ao Inspetor"
                description="A inspeção será devolvida ao inspetor original para correção do checklist. Deseja continuar?"
                onConfirm={onRedirectToInspector}
                okText="Sim, devolver"
                cancelText="Cancelar"
              >
                <Button icon={<RollbackOutlined />}>
                  Devolver ao Inspetor
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>
      </Card>
    );
  }

  return null;
}
